import React, { useState, useEffect } from 'react';
import { 
  Plus, Trash2, Eye, ArrowLeft, Users, Calendar, Clock, CheckCircle, 
  MessageSquare, Edit, RotateCcw, Send, X, Search, Filter
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const TaskManagerNew = () => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('active');
  const [showBuilder, setShowBuilder] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [searchDeptQuery, setSearchDeptQuery] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [filterAssignee, setFilterAssignee] = useState('all');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium',
    start_date: '',
    end_date: '',
    target_audience: {
      type: 'whole_company',
      departments: [],
      employee_ids: []
    }
  });

  useEffect(() => {
    fetchTasks();
    fetchDepartments();
    fetchEmployees();
  }, []);

  useEffect(() => {
    if (searchQuery && formData.target_audience.type === 'individual') {
      const filtered = employees.filter(emp =>
        `${emp.first_name} ${emp.last_name}`.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredEmployees(filtered);
    } else {
      setFilteredEmployees([]);
    }
  }, [searchQuery, employees, formData.target_audience.type]);

  const fetchTasks = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tasks/manager/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setTasks(data);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
  };

  const fetchDepartments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/departments`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setDepartments(data);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      // Fetch all employees company-wide for task assignment (not just manager's department)
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/employees?all_employees=true`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setEmployees(data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchTaskDetails = async (taskId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tasks/${taskId}/details`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setSelectedTask(data);
      setShowTaskModal(true);
      
      // Mark task notifications as read
      try {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/mark-task-read/${taskId}`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        // Trigger notification refresh
        window.dispatchEvent(new Event('refreshNotifications'));
      } catch (notifError) {
        console.error('Failed to mark notifications as read:', notifError);
      }
    } catch (error) {
      console.error('Error fetching task details:', error);
      toast.error('Failed to load task details');
    }
  };

  const createTask = async () => {
    if (!formData.title || !formData.description) {
      toast.error('Please fill in title and description');
      return;
    }

    if (formData.start_date && formData.end_date) {
      const start = new Date(formData.start_date);
      const end = new Date(formData.end_date);
      if (end < start) {
        toast.error('End date cannot be before start date');
        return;
      }
    }

    if (formData.target_audience.type === 'by_department' && formData.target_audience.departments.length === 0) {
      toast.error('Please select at least one department');
      return;
    }

    if (formData.target_audience.type === 'individual' && formData.target_audience.employee_ids.length === 0) {
      toast.error('Please select at least one employee');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tasks/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        setShowBuilder(false);
        resetForm();
        fetchTasks();
      } else {
        const error = await response.json();
        toast.error(`Error: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error creating task:', error);
      toast.error('Failed to create task');
    }
    setLoading(false);
  };

  const deleteTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tasks/${taskId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Task deleted successfully');
        fetchTasks();
      }
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error('Failed to delete task');
    }
  };

  const reopenTask = async (taskId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tasks/${taskId}/reopen`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Task reopened successfully');
        fetchTasks();
        setShowTaskModal(false);
        setSelectedTask(null);
      } else {
        const error = await response.json();
        toast.error(error.detail);
      }
    } catch (error) {
      console.error('Error reopening task:', error);
      toast.error('Failed to reopen task');
    }
  };

  const sendReply = async () => {
    if (!replyMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tasks/${selectedTask.task.id}/reply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ message: replyMessage })
      });

      if (response.ok) {
        toast.success('Reply sent');
        setReplyMessage('');
        fetchTaskDetails(selectedTask.task.id);
      } else {
        const error = await response.json();
        toast.error(error.detail);
      }
    } catch (error) {
      console.error('Error sending reply:', error);
      toast.error('Failed to send reply');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      priority: 'medium',
      start_date: '',
      end_date: '',
      target_audience: {
        type: 'whole_company',
        departments: [],
        employee_ids: []
      }
    });
    setSearchQuery('');
    setSearchDeptQuery('');
  };

  const toggleEmployeeSelection = (employeeId) => {
    const currentIds = formData.target_audience.employee_ids;
    const newIds = currentIds.includes(employeeId)
      ? currentIds.filter(id => id !== employeeId)
      : [...currentIds, employeeId];
    
    setFormData({
      ...formData,
      target_audience: { ...formData.target_audience, employee_ids: newIds }
    });
  };

  const filteredTasks = tasks
    .filter(task => {
      // Filter by tab (active/completed)
      if (activeTab === 'active' && task.status !== 'active') return false;
      if (activeTab === 'completed' && task.status !== 'completed') return false;
      
      // Filter by assignee
      if (filterAssignee !== 'all' && filterAssignee.trim()) {
        const assigneeNames = task.assigned_to_names || [];
        if (!assigneeNames.some(name => name.toLowerCase().includes(filterAssignee.toLowerCase()))) {
          return false;
        }
      }
      
      // Filter by start date
      if (filterStartDate) {
        if (!task.start_date || new Date(task.start_date) < new Date(filterStartDate)) {
          return false;
        }
      }
      
      // Filter by end date
      if (filterEndDate) {
        if (!task.end_date || new Date(task.end_date) > new Date(filterEndDate)) {
          return false;
        }
      }
      
      // Filter by status
      if (filterStatus !== 'all') {
        if (task.status !== filterStatus) return false;
      }
      
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'date') {
        const dateA = new Date(a.start_date || a.created_at);
        const dateB = new Date(b.start_date || b.created_at);
        return dateB - dateA;
      } else if (sortBy === 'assignee') {
        const nameA = (a.assigned_to_names || [])[0] || '';
        const nameB = (b.assigned_to_names || [])[0] || '';
        return nameA.localeCompare(nameB);
      }
      return 0;
    });

  const priorityColors = {
    high: { bg: 'bg-red-50', border: 'border-red-500', text: 'text-red-700', badge: 'bg-red-500' },
    medium: { bg: 'bg-yellow-50', border: 'border-yellow-500', text: 'text-yellow-700', badge: 'bg-yellow-500' },
    low: { bg: 'bg-blue-50', border: 'border-blue-500', text: 'text-blue-700', badge: 'bg-blue-500' }
  };

  const filteredDepartments = departments.filter(dept =>
    dept.toLowerCase().includes(searchDeptQuery.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header with inline filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
        <div className="flex flex-col gap-4">
          {/* Back Button */}
          <Button onClick={() => navigate('/admin/dashboard')} variant="ghost" size="sm" className="self-start">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>

          {/* Page Title */}
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Task Management</h1>
            <p className="text-sm text-gray-500 mt-1">Assign and track employee tasks</p>
          </div>

          {/* Horizontal Filter Bar */}
          <div className="flex flex-wrap gap-3 items-end">
            {/* Start Date Filter */}
            <div className="flex-1 min-w-[150px]">
              <label className="block text-xs font-medium text-gray-700 mb-1">Start Date</label>
              <div className="relative">
                <input
                  type="date"
                  value={filterStartDate}
                  onChange={(e) => setFilterStartDate(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
                <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {/* Due Date Filter */}
            <div className="flex-1 min-w-[150px]">
              <label className="block text-xs font-medium text-gray-700 mb-1">Due Date</label>
              <div className="relative">
                <input
                  type="date"
                  value={filterEndDate}
                  onChange={(e) => setFilterEndDate(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
                <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {/* Assigned To Filter */}
            <div className="flex-1 min-w-[150px]">
              <label className="block text-xs font-medium text-gray-700 mb-1">Assigned To</label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search by name..."
                  value={filterAssignee}
                  onChange={(e) => setFilterAssignee(e.target.value)}
                  className="w-full px-3 py-2 pr-9 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
                <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {/* Status Filter */}
            <div className="flex-1 min-w-[150px]">
              <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              >
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="completed">Completed</option>
              </select>
            </div>

            {/* Clear Filters Button */}
            <Button
              onClick={() => {
                setFilterStartDate('');
                setFilterEndDate('');
                setFilterAssignee('all');
                setFilterStatus('all');
              }}
              variant="outline"
              className="whitespace-nowrap h-[42px] px-4"
            >
              <X className="w-4 h-4 mr-1" />
              Clear
            </Button>

            {/* New Task Button */}
            <Button 
              onClick={() => setShowBuilder(true)} 
              className="bg-teal-600 hover:bg-teal-700 text-white whitespace-nowrap h-[42px] px-4"
            >
              <Plus className="w-4 h-4 mr-1" />
              New Task
            </Button>
          </div>
        </div>
      </div>

      {/* Task Creation Modal */}
      {showBuilder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 sm:p-6 flex items-center justify-between">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Create New Task</h2>
                <p className="text-sm text-gray-500 mt-1">Assign a task to employees</p>
              </div>
              <button
                onClick={() => { setShowBuilder(false); resetForm(); }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Form */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 space-y-6">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Task Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Complete monthly safety checklist"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>

            {/* Notes/Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Notes *</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Provide detailed instructions for the task..."
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>

            {/* Start Date & End Date */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                <input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                <input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  min={formData.start_date || new Date().toISOString().split('T')[0]}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                />
              </div>
            </div>

            {/* Assign To */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Assign To</label>
              <select
                value={formData.target_audience.type}
                onChange={(e) => setFormData({
                  ...formData,
                  target_audience: {
                    type: e.target.value,
                    departments: [],
                    employee_ids: []
                  }
                })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 mb-3"
              >
                <option value="whole_company">Company</option>
                <option value="by_department">Department</option>
                <option value="individual">Individual(s)</option>
              </select>

              {formData.target_audience.type === 'by_department' && (
                <div className="space-y-2">
                  <input
                    type="text"
                    placeholder="Search departments..."
                    value={searchDeptQuery}
                    onChange={(e) => setSearchDeptQuery(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 mb-2"
                  />
                  <div className="max-h-48 overflow-y-auto space-y-2">
                    {filteredDepartments.map(dept => (
                      <label key={dept} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded">
                        <input
                          type="checkbox"
                          checked={formData.target_audience.departments.includes(dept)}
                          onChange={(e) => {
                            const departments = e.target.checked
                              ? [...formData.target_audience.departments, dept]
                              : formData.target_audience.departments.filter(d => d !== dept);
                            setFormData({
                              ...formData,
                              target_audience: { ...formData.target_audience, departments }
                            });
                          }}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm text-gray-700">{dept}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {formData.target_audience.type === 'individual' && (
                <div className="space-y-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Search employees by name..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                  
                  {formData.target_audience.employee_ids.length > 0 && (
                    <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm font-medium text-gray-700">Selected ({formData.target_audience.employee_ids.length}):</span>
                      {formData.target_audience.employee_ids.map(empId => {
                        const emp = employees.find(e => e.id === empId);
                        return emp ? (
                          <span key={empId} className="inline-flex items-center gap-1 px-2 py-1 bg-teal-100 text-teal-700 rounded text-sm">
                            {emp.first_name} {emp.last_name}
                            <X 
                              className="w-3 h-3 cursor-pointer hover:text-teal-900" 
                              onClick={() => toggleEmployeeSelection(empId)}
                            />
                          </span>
                        ) : null;
                      })}
                    </div>
                  )}

                  <div className="max-h-64 overflow-y-auto space-y-1">
                    {(searchQuery ? filteredEmployees : employees).map(emp => (
                      <label 
                        key={emp.id} 
                        className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={formData.target_audience.employee_ids.includes(emp.id)}
                          onChange={() => toggleEmployeeSelection(emp.id)}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm text-gray-700">
                          {emp.first_name} {emp.last_name}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button onClick={() => { setShowBuilder(false); resetForm(); }} variant="outline">
                Cancel
              </Button>
              <Button onClick={createTask} disabled={loading} className="bg-teal-600 hover:bg-teal-700 text-white">
                <Send className="w-4 h-4 mr-2" />
                Send
              </Button>
            </div>
          </div>
        </div>
        </div>
      )}

      {/* Tabs and Task List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('active')}
            className={`flex-1 px-3 sm:px-6 py-3 sm:py-4 text-sm font-medium transition-colors ${
              activeTab === 'active'
                ? 'text-teal-600 border-b-2 border-teal-600 bg-teal-50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            Active Tasks ({tasks.filter(t => t.status === 'active').length})
          </button>
          <button
            onClick={() => setActiveTab('completed')}
            className={`flex-1 px-3 sm:px-6 py-3 sm:py-4 text-sm font-medium transition-colors ${
              activeTab === 'completed'
                ? 'text-teal-600 border-b-2 border-teal-600 bg-teal-50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            Completed Tasks ({tasks.filter(t => t.status === 'completed').length})
          </button>
        </div>

        {/* Task List */}
        <div className="p-4 sm:p-6">
          {filteredTasks.length > 0 ? (
            <div className="space-y-4">
              {filteredTasks.map(task => {
                const style = priorityColors[task.priority];
                return (
                  <div
                    key={task.id}
                    className={`${style.bg} border-l-4 ${style.border} rounded-lg p-3 sm:p-4`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <h3 className="text-base sm:text-lg font-semibold text-gray-900">{task.title}</h3>
                          <span className={`${style.badge} text-white text-xs px-2 py-1 rounded-full whitespace-nowrap`}>
                            {task.priority}
                          </span>
                          {task.status === 'completed' && (
                            <span className="bg-green-500 text-white text-xs px-2 py-1 rounded-full whitespace-nowrap flex items-center gap-1">
                              <CheckCircle className="w-3 h-3" />
                              Completed
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-700 mb-3">{task.description}</p>
                        <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600">
                          {task.assigned_to_names && task.assigned_to_names.length > 0 && (
                            <span className="flex items-center gap-1">
                              <Users className="w-3 h-3" />
                              Assigned to: {task.assigned_to_names.join(', ')}
                            </span>
                          )}
                          {task.start_date && (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              Start: {new Date(task.start_date).toLocaleDateString()}
                            </span>
                          )}
                          {task.end_date && (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              End: {new Date(task.end_date).toLocaleDateString()}
                            </span>
                          )}
                          {task.status === 'completed' && task.completed_at && (
                            <span className="flex items-center gap-1">
                              <CheckCircle className="w-3 h-3" />
                              Completed on: {new Date(task.completed_at).toLocaleDateString()}
                              {task.completed_by_name && ` by ${task.completed_by_name}`}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2 flex-wrap sm:flex-nowrap">
                        <Button
                          onClick={() => fetchTaskDetails(task.id)}
                          variant="outline"
                          size="sm"
                          className="flex-1 sm:flex-none"
                        >
                          <Eye className="w-4 h-4 sm:mr-1" />
                          <span className="hidden sm:inline">View</span>
                        </Button>
                        {task.status === 'completed' && (
                          <Button
                            onClick={() => reopenTask(task.id)}
                            variant="outline"
                            size="sm"
                            className="text-blue-600 hover:bg-blue-50"
                          >
                            <RotateCcw className="w-4 h-4 sm:mr-1" />
                            <span className="hidden sm:inline">Reopen</span>
                          </Button>
                        )}
                        <Button
                          onClick={() => deleteTask(task.id)}
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:bg-red-50 flex-shrink-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <Clock className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500">No {activeTab} tasks</p>
            </div>
          )}
        </div>
      </div>

      {/* Task Detail Modal */}
      {showTaskModal && selectedTask && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 sm:p-6 flex items-center justify-between">
              <div className="flex-1">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900">{selectedTask.task.title}</h2>
                <p className="text-sm text-gray-600 mt-1">
                  {selectedTask.task.status === 'completed' ? 'Completed Task' : 'Active Task'}
                </p>
              </div>
              <button
                onClick={() => { setShowTaskModal(false); setSelectedTask(null); }}
                className="text-gray-400 hover:text-gray-600 ml-4"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-4 sm:p-6">
              {/* Task Info */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <p className="text-gray-700 mb-3">{selectedTask.task.description}</p>
                <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
                  <span className={`px-3 py-1 rounded ${priorityColors[selectedTask.task.priority].badge} text-white`}>
                    {selectedTask.task.priority} priority
                  </span>
                  {selectedTask.task.created_by_name && (
                    <span>Created by: {selectedTask.task.created_by_name}</span>
                  )}
                  {selectedTask.task.assigned_to_names && (
                    <span>Assigned to: {selectedTask.task.assigned_to_names.join(', ')}</span>
                  )}
                  {selectedTask.task.start_date && (
                    <span>Start: {new Date(selectedTask.task.start_date).toLocaleDateString()}</span>
                  )}
                  {selectedTask.task.end_date && (
                    <span>End: {new Date(selectedTask.task.end_date).toLocaleDateString()}</span>
                  )}
                </div>
              </div>

              {/* Replies Section */}
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                <MessageSquare className="w-5 h-5 inline mr-2" />
                Conversation
              </h3>
              
              <div className="space-y-3 mb-6">
                {selectedTask.replies && selectedTask.replies.length > 0 ? (
                  selectedTask.replies.map(reply => (
                    <div key={reply.id} className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-2">
                        <span className="font-medium text-gray-900">{reply.user_name}</span>
                        <span className="text-xs text-gray-500">
                          {new Date(reply.created_at).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700">{reply.message}</p>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 italic">No replies yet</p>
                )}
              </div>

              {/* Reply Input (only for active tasks) */}
              {selectedTask.task.status === 'active' && (
                <div className="border-t pt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Add Reply</label>
                  <textarea
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                    placeholder="Type your reply..."
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent mb-3"
                  />
                  <Button onClick={sendReply} className="bg-teal-600 hover:bg-teal-700 text-white w-full sm:w-auto">
                    <Send className="w-4 h-4 mr-2" />
                    Send Reply
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskManagerNew;
